import {
  JOB_APPLICATION_MATCH_STATUS,
  MEMBER_REJECTION_REASONS,
  REJECTION_REASONS,
} from "lookup";
import React, { useContext, useEffect, useState } from "react";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import FormLabel from "components/base/FormLabel";
import TextArea from "components/base/TextArea";
import { MatchContext } from "context/providers";

const TYPES_OF_REJECTION = [
  {
    label: "Please select an option...",
    value: "",
  },
  {
    label: "Customer",
    value: JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER,
  },
  {
    label: "Member",
    value: JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER,
  },
  {
    label: "Matcher",
    value: JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER,
  },
];

const options = [
  ...Object.values(REJECTION_REASONS).map(({ title, value }) => ({
    value,
    label: title,
  })),
];

const memberOptions = [
  ...Object.keys(MEMBER_REJECTION_REASONS).map((key) => ({
    value: key,
    label: MEMBER_REJECTION_REASONS[key],
  })),
];

const selectComponents = {
  IndicatorSeparator: () => null,
  DropdownIndicator: (props) => (
    <components.DropdownIndicator {...props}>
      <ChevronDown className="h-4 w-4 text-slate-500" />
    </components.DropdownIndicator>
  ),
};

const selectStyles = {
  valueContainer: (base) => ({
    ...base,
    padding: "8px 12px",
  }),
  // isMulti keeps the search input inline, and its own margin/padding would
  // make the control taller than the single-value selects.
  input: (base) => ({
    ...base,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const RejectionForm = ({
  applicationId,
  jobOpportunityId,
  isVisible,
  onClose,
}) => {
  const [feedback, setFeedback] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [typeOfRejection, setTypeOfRejection] = useState(TYPES_OF_REJECTION[0]);
  const { updateMatch } = useContext(MatchContext);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const submitEnabled = !!selectedReasons?.length;

  useEffect(() => {
    if (isVisible) {
      resetForm();
    }
  }, [isVisible]);

  const resetForm = () => {
    setSelectedReasons([]);
    setFeedback("");
    setTypeOfRejection(TYPES_OF_REJECTION[0]);
  };

  const handleSelectTypeOfRejection = (el) => {
    setSelectedReasons([]);
    setTypeOfRejection(el);
  };

  const handleChangeReasons = (selectedOptions) => {
    setSelectedReasons(selectedOptions);
  };

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value);
  };

  const handleSubmit = async (e) => {
    setIsSubmiting(true);
    const reasonForRejection = selectedReasons.map((el) => el.value);
    const data = {
      status: typeOfRejection.value,
      reasonForRejection,
      rejectionByCustomer: feedback.trim(),
    };
    await updateMatch(applicationId, jobOpportunityId, data);
    onClose();
    setIsSubmiting(false);
  };

  return (
    <div
      style={{
        minHeight: "500px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "4px",
        }}
      >
        <FormLabel className="mt-4" htmlFor="rejected-by" required>
          Rejected by
        </FormLabel>
        <Select
          inputId="rejected-by"
          value={typeOfRejection}
          options={TYPES_OF_REJECTION}
          className="mt-2 w-full"
          onChange={handleSelectTypeOfRejection}
          isSearchable={false}
          components={selectComponents}
          styles={selectStyles}
        />
      </div>

      <div>
        <FormLabel className="mt-4" htmlFor="rejection-reasons" required>
          Reasons for Rejection
        </FormLabel>
        <div className="mt-2">
          {typeOfRejection.value ===
          JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER ? (
            <Select
              inputId="rejection-reasons"
              value={selectedReasons}
              options={memberOptions}
              isMulti
              className="w-full"
              onChange={handleChangeReasons}
              components={selectComponents}
              styles={selectStyles}
            />
          ) : (
            <Select
              inputId="rejection-reasons"
              value={selectedReasons}
              options={options}
              isMulti
              className="w-full"
              onChange={handleChangeReasons}
              isDisabled={!typeOfRejection?.value}
              components={selectComponents}
              styles={selectStyles}
            />
          )}
        </div>
      </div>
      <FormLabel className="mt-4" htmlFor="optional-feedback">
        Optional feedback
      </FormLabel>
      <TextArea
        id="optional-feedback"
        className="-mt-4"
        value={feedback}
        onChange={(e) => handleFeedbackChange(e)}
      />
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-sky-500 px-4 py-3 font-inter text-sm font-medium text-white transition hover:bg-sky-600 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!submitEnabled || isSubmiting}
        >
          {isSubmiting ? "Submiting..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default RejectionForm;
